import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";

interface MaterialitySummaryProps {
  planningData: any;
  abcotdData: any;
  componentData: any;
  revisedMaterialityData?: any;
}

/**
 * Summary component displaying key materiality metrics and additional materiality types.
 * Shows planning materiality, performance materiality, trivial threshold, and benchmarks used.
 * Uses revised materiality values if available, otherwise falls back to planning values.
 */
export function MaterialitySummary({
  planningData,
  abcotdData,
  componentData,
  revisedMaterialityData,
}: MaterialitySummaryProps) {
  if (!planningData) return null;

  // Use revised materiality if available, otherwise use planning materiality
  const hasRevisedMateriality = revisedMaterialityData && revisedMaterialityData.determinedMateriality > 0;
  const materiality = hasRevisedMateriality 
    ? revisedMaterialityData.determinedMateriality 
    : planningData.determinedMateriality;

  const performanceMateriality = hasRevisedMateriality
    ? (revisedMaterialityData.performanceMateriality || 
       (materiality * (planningData.performancePercentage || 75)) / 100)
    : (planningData.calculatedPerformanceMateriality > 0
        ? planningData.calculatedPerformanceMateriality
        : (planningData.determinedMateriality * planningData.performancePercentage) / 100);

  const trivialThreshold = hasRevisedMateriality
    ? (revisedMaterialityData.trivialThreshold || 
       (materiality * (planningData.trivialPercentage || 5)) / 100)
    : (planningData.trivialThreshold ||
       (planningData.determinedMateriality * planningData.trivialPercentage) / 100);

  const isCustomPerformanceMateriality =
    !hasRevisedMateriality && planningData.calculatedPerformanceMateriality > 0;

  const isCustomTrivialThreshold =
    !hasRevisedMateriality &&
    planningData.trivialThreshold !==
    (planningData.determinedMateriality * planningData.trivialPercentage) / 100;

  const hasAdditionalMaterialityTypes =
    (abcotdData && abcotdData.abcotdRequired === "Yes") ||
    (componentData && componentData.componentMaterialityRequired);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Materiality Summary</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="space-y-1">
            <p className="text-muted-foreground text-sm">
              {hasRevisedMateriality ? "Revised Materiality" : "Planning Materiality"}
            </p>
            <p className="text-2xl font-bold">
              {materiality.toLocaleString('en-IN')}
            </p>
            {hasRevisedMateriality && (
              <p className="text-muted-foreground text-xs">
                Planning: {planningData.determinedMateriality.toLocaleString('en-IN')}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <p className="text-muted-foreground text-sm">
              Performance Materiality
            </p>
            <p className="text-2xl font-bold">
              {performanceMateriality.toLocaleString('en-IN')}
            </p>
            <p className="text-muted-foreground text-xs">
              {hasRevisedMateriality
                ? `${planningData.performancePercentage}% of revised materiality`
                : isCustomPerformanceMateriality
                ? "Custom value"
                : `${planningData.performancePercentage}% of planning materiality`}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-muted-foreground text-sm">Trivial Threshold</p>
            <p className="text-2xl font-bold">
              {trivialThreshold.toLocaleString('en-IN')}
            </p>
            <p className="text-muted-foreground text-xs">
              {hasRevisedMateriality
                ? `${planningData.trivialPercentage}% of revised materiality`
                : isCustomTrivialThreshold
                ? "Custom value"
                : `${planningData.trivialPercentage}% of planning materiality`}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-muted-foreground text-sm">Benchmarks Used</p>
            <p className="text-2xl font-bold">
              {planningData.selectedBenchmarks?.length || 0}
            </p>
            <p className="text-muted-foreground text-xs">
              {planningData.selectedBenchmarks?.join(", ") || "None"}
            </p>
          </div>
        </div>

        {/* Additional Materiality Types */}
        {hasAdditionalMaterialityTypes && (
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            {abcotdData && abcotdData.abcotdRequired === "Yes" && (
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">
                  ABCOTD Materiality
                </p>
                <p className="text-lg font-bold">
                  {(abcotdData.abcotdDetails || []).length} items
                </p>
                <p className="text-muted-foreground text-xs">
                  Account Balances, Classes, Disclosures
                </p>
              </div>
            )}

            {componentData && componentData.componentMaterialityRequired && (
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">
                  Component Materiality
                </p>
                <p className="text-lg font-bold">
                  {(componentData.componentDetails || []).length} components
                </p>
                <p className="text-muted-foreground text-xs">
                  Group audit components
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
